import {Component, OnDestroy, OnInit} from '@angular/core';
import {Update} from '@ngrx/entity';
import {Observable, Subscription} from 'rxjs';
import {ActivatedRoute, Router} from '@angular/router';
import {SubheaderService} from '../../../../core/_base/layout';
import {AppState} from '../../../../core/reducers';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {select, Store} from '@ngrx/store';
import {delay} from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import {
    FoundationType,
    FoundationTypeOnServerCreated,
    FoundationTypeUpdated,
    selectFoundationTypeById,
    selectFoundationTypesActionLoading,
    selectLastCreatedFoundationTypeId
} from '../../../../core/linked-tables';
import {Location} from '@angular/common';
import { combineLatest } from 'rxjs';

@Component({
    selector: 'kt-foundation-type-edit',
    templateUrl: './foundation-type-edit.component.html',
    styleUrls: ['./foundation-type-edit.component.scss']
})


export class FoundationTypeEditComponent implements OnInit, OnDestroy {

    model: FoundationType;
    oldModel: FoundationType;

    foundationTypeForm: UntypedFormGroup;
    hasFormErrors = false;

    loading$: Observable<boolean>;
    viewLoading = false;
    loadingAfterSubmit = false;
    mode = 'NEW';
    readonly = true
    // Private properties
    private componentSubscriptions: Subscription;
    private routeSubscription: Subscription;

    /**
     * Component constructor
     *
     * @param activatedRoute: ActivatedRoute
     * @param router: Router
     * @param fb: FormBuilder
     * @param layoutUtilsService: LayoutUtilsService
     * @param subheaderService: SubheaderService
     * @param store: Store<AppState>
     * @param location: Location
     * @param layoutConfigService: LayoutConfigService
     */
    constructor(private activatedRoute: ActivatedRoute,
                private router: Router,
                private fb: UntypedFormBuilder,
                public subheaderService: SubheaderService,
                private store: Store<AppState>,
                private location: Location,
                private translate: TranslateService) {
    }

    ngOnInit() {
        this.loading$ = this.store.pipe(select(selectFoundationTypesActionLoading));
        this.routeSubscription = combineLatest([
            this.activatedRoute.params,
            this.activatedRoute.data
        ]).subscribe(([params, data]) => {
            const id = params.id;
            this.readonly = data.readonly
            if (id && id > 0) {
                this.store.pipe(select(selectFoundationTypeById(id))).subscribe(res => {
                    if (res) {
                        this.oldModel = Object.assign({}, res);
                        this.model = Object.assign({}, this.oldModel);
                        this.initFoundationType();
                    }
                });
            } else {
                this.model = new FoundationType();
                this.model.clear();
                this.oldModel = Object.assign({}, this.model);
                this.initFoundationType();
            }
        });

        if (this.activatedRoute.snapshot.paramMap.get('id') != null) {
            this.setBreadCrumbs(Number(this.activatedRoute.snapshot.paramMap.get('id')));
        } else {
            this.setBreadCrumbs(0);
        }
    }

    setBreadCrumbs(id: number): void {
        this.subheaderService.setTitle(this.getComponentTitle());
        this.subheaderService.setBreadcrumbs([
            {title: 'Developer', page: `../default/admin-management`},
            {title: 'Linked tables', page: `../default/admin-management/linked-tables`},
            {title: 'Foundation Types', page: `../default/admin-management/linked-tables/base-of-values`},
            {
                title: this.getComponentTitle(),
                page: id > 0 ? `../default/admin-management/linked-tables/base-of-values/edit/${id}`
                    : `../default/admin-management/linked-tables/base-of-values/add`
            }
        ]);
    }

    ngOnDestroy(): void {
        if (this.componentSubscriptions) {
            this.componentSubscriptions.unsubscribe();
        }
        if (this.routeSubscription) {
            this.routeSubscription.unsubscribe();
        }
    }

    initFoundationType() {
        this.createForm();
    }


    createForm() {
        this.foundationTypeForm = this.fb.group({
            name: [this.model.name, Validators.required],
            desc: [this.model.desc, Validators.required],
        });
    }

    /**
     * Returns page title
     */
    getComponentTitle(): string {
        if (this.model && this.model.id > 0) {
            this.mode = 'EDIT';
            return `Edit Foundation Type '${
                this.model.name
                }'`;
        }

        return 'New Foundation Type';
    }

    /**
     * Check control is invalid
     * @param controlName: string
     */
    isControlInvalid(controlName: string): boolean {
        const control = this.foundationTypeForm.controls[controlName];
        return control.invalid && control.touched;
    }

    reset() {
        this.model = Object.assign({}, this.oldModel);
        this.createForm();
        this.hasFormErrors = false;
        this.foundationTypeForm.markAsPristine();
        this.foundationTypeForm.markAsUntouched();
        this.foundationTypeForm.updateValueAndValidity();
    }

    /** ACTIONS */

    /**
     * On Submit
     */
    onSubmit(withBack: boolean = false) {
        this.hasFormErrors = false;
        const controls = this.foundationTypeForm.controls;
        /** check form */
        if (this.foundationTypeForm.invalid) {
            Object.keys(controls).forEach(controlName =>
                controls[controlName].markAsTouched()
            );

            this.hasFormErrors = true;
            return;
        }

        if (this.model.id > 0) {
            this.updateFoundationType(this.model);
        } else {
            this.createFoundationType(this.model);
        }
    }

    /**
     * Update model
     *
     * @param _model: FoundationTypeModel
     */
    updateFoundationType(_model: FoundationType) {
        this.loadingAfterSubmit = true;
        this.viewLoading = true;

        const updateFoundationType: Update<FoundationType> = {
            id: _model.id,
            changes: _model
        };
        this.store.dispatch(new FoundationTypeUpdated({
            partialFoundationType: updateFoundationType,
            foundationType: _model
        }));
        this.oldModel = _model;
        this.navigateToParent();

    }

    /**
     * Create model
     *
     * @param _model: FoundationTypeModel
     */
    createFoundationType(_model: FoundationType) {
        this.store.dispatch(new FoundationTypeOnServerCreated({foundationType: _model}));
        this.oldModel = _model;
        this.componentSubscriptions = this.store.pipe(
            select(selectLastCreatedFoundationTypeId),
            delay(1000), // Remove this line
        ).subscribe(res => {
            if (!res) {
                return;
            }
            this.navigateToParent();
        });
    }

    /** Alect Close event */
    onAlertClose($event) {
        this.hasFormErrors = false;
    }


    isFormValid() {
        return (this.model && this.model.name && this.model.name.length > 0);
    }

    navigateToParent() {
        let url = '../';
        if (this.model.id > 0) {
            url = '../../';
        }
        this.router.navigate([url], {relativeTo: this.activatedRoute});
    }

    canDeactivate() {
        if (this.discard()) {
            if (window.confirm('Are you sure? All unsaved changes will be lost.')) {
                return true;
            } else {
                // ---------work around angular bug--------- reference: https://github.com/angular/angular/issues/13586
                const currentUrlTree = this.router.createUrlTree([], this.activatedRoute.snapshot);
                const currentUrl = currentUrlTree.toString();
                this.location.go(currentUrl);
                // ---------work around end-----------------
                return false;
            }
        }
        return true;
    }

    discard() {
        if (this.model && this.oldModel) {
            return this.model.name != this.oldModel.name || this.model.desc != this.oldModel.desc;
        }
        return false;
    }

}
